import {
  Button,
  Box,
  Paper,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { observer } from 'mobx-react';
import { useCompany } from '../../../stores';
import { useBillingItemsQuery } from '../../../stores/FetcherStores/BillingItemFetcher';
import { BillingItem, BillingItemType } from '../../../model/BillingItem';
import { filterBillingItemsForMonth, getBillingItemTitle } from '../../../utils/billingItem';
import AddSubscriptionDrawer from '../../../components/AddSubscriptionDrawer';
import EditSubscriptionDrawer from './EditSubscriptionDrawer';
import { formatEndDate } from '../../../utils/date';

const ActivePackagesSection = observer(() => {
  const { t } = useTranslation();
  const companyStore = useCompany();
  const [subscriptionDrawerOpen, setSubscriptionDrawerOpen] = useState(false);
  const [selectedBillingItemId, setSelectedBillingItemId] = useState<string | null>(null);
  const { data: billingItems, isLoading } = useBillingItemsQuery(companyStore.companyKey || '');

  const monthString = DateTime.now().toFormat('yyyy-MM');
  const activeOrFutureBillingItems = filterBillingItemsForMonth(billingItems || [], monthString);

  const formatDate = (date: string | null | undefined) => {
    if (!date) return '-';
    return DateTime.fromISO(date).toFormat('yyyy-MM-dd');
  };

  if (!companyStore.companyKey) {
    return null;
  }
  return (
    <>
      <Paper sx={{ mt: 3 }}>
        <Box sx={{ p: 2 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="h2" gutterBottom sx={{ mb: 0 }}>
              Aktiva paket
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setSubscriptionDrawerOpen(true)}
              sx={{
                backgroundColor: '#1db954',
                '&:hover': {
                  backgroundColor: '#1a9f4a',
                },
              }}
            >
              Lägg till paket
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Namn</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Startdatum</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Slutdatum</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">
                    Antal
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">
                    Styckpris
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">
                    Ändra
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  if (isLoading) {
                    return (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          Laddar...
                        </TableCell>
                      </TableRow>
                    );
                  }

                  if (activeOrFutureBillingItems.length === 0) {
                    return (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          Inga aktiva paket
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return activeOrFutureBillingItems.map((item: BillingItem) => (
                    <TableRow key={item.id}>
                      <TableCell>{getBillingItemTitle(item, t)}</TableCell>
                      <TableCell>
                        {formatDate('startDate' in item ? item.startDate : undefined)}
                      </TableCell>
                      <TableCell>
                        {'endDate' in item ? formatEndDate(item.endDate) : undefined}
                      </TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">
                        {item.unitPrice.applyPercentageDiscount(item.discount).format()}
                      </TableCell>
                      <TableCell align="right">
                        {item.type === BillingItemType.SUBSCRIPTION &&
                          (!item.endDate ||
                            DateTime.fromISO(item.endDate).diffNow().toMillis() > 0) && (
                            <IconButton
                              size="small"
                              onClick={() => setSelectedBillingItemId(item.id)}
                            >
                              <EditIcon />
                            </IconButton>
                          )}
                      </TableCell>
                    </TableRow>
                  ));
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      <AddSubscriptionDrawer
        open={subscriptionDrawerOpen}
        onClose={() => setSubscriptionDrawerOpen(false)}
      />
      <EditSubscriptionDrawer
        open={!!selectedBillingItemId}
        onClose={() => setSelectedBillingItemId(null)}
        billingItemId={selectedBillingItemId}
      />
    </>
  );
});

export default ActivePackagesSection;
